import React, { useState, useMemo } from 'react';
import {
  Text,
  Button,
  Badge,
  Input,
  Tooltip,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { Add20Regular, Search20Regular } from '@fluentui/react-icons';
import type { DatasetViewColumnItem } from '../../api/dataModelsService';
import {
  classifyColumn,
  categoryLabel,
  categoryBadgeColor,
  dataTypeLabel,
  fieldDescription,
  type FieldCategory,
} from './fieldUtils';
import FieldCategoryIcon from './FieldCategoryIcon';
import JsonProvenanceBadge from '../edit/fieldMapping/fieldPicker/JsonProvenanceBadge';

// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  fieldsSticky: {
    position: 'sticky',
    top: '0px',
    zIndex: 10,
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.padding('12px', '33px', '8px'),
    ...shorthands.margin('0', '-33px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke3),
    boxShadow: '0 2px 4px -2px rgba(0,0,0,0.08)',
  },
  fieldsToolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },
  fieldsTitle: {
    fontSize: '11px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap',
  },
  fieldsFilterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flexWrap: 'wrap',
  },
  filterChip: {
    cursor: 'pointer',
    userSelect: 'none',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
  },
  thead: {
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke3),
    position: 'sticky',
    top: '45px',
    zIndex: 9,
    backgroundColor: tokens.colorNeutralBackground3,
    boxShadow: '0 1px 0 0 rgba(0,0,0,0.06)',
  },
  th: {
    ...shorthands.padding('7px', '10px'),
    fontSize: '11px',
    fontWeight: 700,
    color: tokens.colorNeutralForeground2,
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    textAlign: 'left',
  },
  colName: { width: '25%' },
  colCategory: { width: '13%' },
  colDescription: { width: '35%' },
  colSource: { width: '15%' },
  colType: { width: '12%', textAlign: 'right' },
  row: {
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke3),
    transitionProperty: 'background-color',
    transitionDuration: '100ms',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  rowCalculated: {
    borderLeftWidth: '2px',
    borderLeftStyle: 'solid',
    borderLeftColor: tokens.colorPalettePurpleBorderActive,
    backgroundColor: tokens.colorPalettePurpleBackground2,
  },
  td: {
    ...shorthands.padding('8px', '10px'),
    fontSize: '13px',
    color: tokens.colorNeutralForeground1,
    verticalAlign: 'middle',
  },
  fieldNameCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  sourceCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
  },
  fieldIcon: {
    color: tokens.colorNeutralForeground3,
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  fieldIconCalc: {
    color: tokens.colorPalettePurpleForeground2,
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  descriptionCell: {
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  dataTypeCell: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    textAlign: 'right',
  },
  // Skeleton
  skeletonRow: {
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke3),
  },
  skeletonCell: {
    ...shorthands.padding('10px'),
  },
  skeletonBar: {
    height: '12px',
    ...shorthands.borderRadius('6px'),
    backgroundColor: tokens.colorNeutralBackground4,
    animationName: {
      '0%': { opacity: 0.5 },
      '50%': { opacity: 1 },
      '100%': { opacity: 0.5 },
    },
    animationDuration: '1.4s',
    animationIterationCount: 'infinite',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    ...shorthands.padding('20px', '0'),
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    fontSize: '13px',
  },
});

// ─── Component ───────────────────────────────────────────────────────────────

interface FieldsTableProps {
  columns: DatasetViewColumnItem[];
  loading: boolean;
  entityDisplayName: (name: string) => string;
}

const SKELETON_ROWS = 6;
const FILTER_CATEGORIES: FieldCategory[] = ['all', 'dimension', 'measure', 'date', 'calculated'];

const FieldsTable: React.FC<FieldsTableProps> = ({ columns, loading, entityDisplayName }) => {
  const styles = useStyles();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<FieldCategory>('all');

  const categoryCounts = useMemo(() => {
    const counts: Record<FieldCategory, number> = { all: columns.length, dimension: 0, measure: 0, date: 0, calculated: 0 };
    for (const col of columns) {
      counts[classifyColumn(col)]++;
    }
    return counts;
  }, [columns]);

  const filteredColumns = useMemo(() => {
    let result = columns;
    if (activeFilter !== 'all') {
      result = result.filter((c) => classifyColumn(c) === activeFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (c) =>
          (c.display_name || c.name).toLowerCase().includes(q) ||
          (c.source_entity || '').toLowerCase().includes(q) ||
          (c.source_entity ? entityDisplayName(c.source_entity) : '').toLowerCase().includes(q)
      );
    }
    return result;
  }, [columns, activeFilter, searchQuery, entityDisplayName]);

  return (
    <>
      {/* Sticky toolbar */}
      <div className={styles.fieldsSticky}>
        <div className={styles.fieldsToolbar}>
          <Text className={styles.fieldsTitle}>Fields ({columns.length})</Text>
          <Input
            size="small"
            placeholder="Search fields..."
            contentBefore={<Search20Regular />}
            value={searchQuery}
            onChange={(_, data) => setSearchQuery(data.value)}
            style={{ width: '200px' }}
          />
          <div className={styles.fieldsFilterRow}>
            {FILTER_CATEGORIES.map((cat) => (
              <Badge
                key={cat}
                appearance={activeFilter === cat ? 'filled' : 'outline'}
                color={activeFilter === cat ? 'brand' : 'informative'}
                size="medium"
                className={styles.filterChip}
                onClick={() => setActiveFilter(cat)}
              >
                {cat === 'all'
                  ? `All (${categoryCounts.all})`
                  : `${cat.charAt(0).toUpperCase() + cat.slice(1)}s (${categoryCounts[cat]})`}
              </Badge>
            ))}
          </div>
          <Button size="small" icon={<Add20Regular />} appearance="subtle">
            Add Calculated Field
          </Button>
        </div>
      </div>

      {/* Table content */}
      {loading ? (
        <table className={styles.table}>
          <tbody>
            {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
              <tr key={i} className={styles.skeletonRow}>
                <td className={styles.skeletonCell}><div className={styles.skeletonBar} style={{ width: '140px' }} /></td>
                <td className={styles.skeletonCell}><div className={styles.skeletonBar} style={{ width: '70px' }} /></td>
                <td className={styles.skeletonCell}><div className={styles.skeletonBar} style={{ width: '180px' }} /></td>
                <td className={styles.skeletonCell}><div className={styles.skeletonBar} style={{ width: '80px' }} /></td>
                <td className={styles.skeletonCell}><div className={styles.skeletonBar} style={{ width: '50px' }} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : filteredColumns.length === 0 ? (
        <div className={styles.emptyState}>No fields match your filter.</div>
      ) : (
        <table className={styles.table}>
          <thead className={styles.thead}>
            <tr>
              <th className={`${styles.th} ${styles.colName}`}>Field Name</th>
              <th className={`${styles.th} ${styles.colCategory}`}>Category</th>
              <th className={`${styles.th} ${styles.colDescription}`}>Description</th>
              <th className={`${styles.th} ${styles.colSource}`}>Source</th>
              <th className={`${styles.th} ${styles.colType}`}>Type</th>
            </tr>
          </thead>
          <tbody>
            {filteredColumns.map((col, i) => {
              const cat = classifyColumn(col);
              const desc = fieldDescription(col);
              return (
                <tr
                  key={i}
                  className={`${styles.row} ${col.is_calculated ? styles.rowCalculated : ''}`}
                >
                  <td className={styles.td}>
                    <span className={styles.fieldNameCell}>
                      <FieldCategoryIcon
                        cat={cat}
                        className={cat === 'calculated' ? styles.fieldIconCalc : styles.fieldIcon}
                      />
                      <span style={{ fontWeight: col.is_calculated ? 600 : 400 }}>
                        {col.display_name || col.name}
                      </span>
                    </span>
                  </td>
                  <td className={styles.td}>
                    <Badge appearance="tint" size="medium" color={categoryBadgeColor(cat)}>
                      {categoryLabel(cat)}
                    </Badge>
                  </td>
                  <td className={styles.td}>
                    {desc ? (
                      <Tooltip content={desc} relationship="description">
                        <span className={styles.descriptionCell}>{desc}</span>
                      </Tooltip>
                    ) : (
                      <span className={styles.descriptionCell}>—</span>
                    )}
                  </td>
                  <td className={styles.td}>
                    <span className={styles.sourceCell}>
                      <span>
                        {col.is_calculated ? 'Calculated' : col.source_entity ? entityDisplayName(col.source_entity) : '—'}
                      </span>
                      {col.source_kind === 'json' && <JsonProvenanceBadge column={col} />}
                    </span>
                  </td>
                  <td className={`${styles.td} ${styles.dataTypeCell}`}>
                    {dataTypeLabel(col)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </>
  );
};

export default FieldsTable;